import { Link } from '@tanstack/react-router';
import HomeHelp from '@/components/help/HomeHelp';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface MenuItem {
  name: string;
  href: string;
  icon?: any;
  active?: boolean;
  items?: Omit<MenuItem, 'icon' | 'items'>[];
  className?: string;
  disabled?: boolean;
  label?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface NavigationProps {
  activePathname: string | null;
  collapsed?: boolean;
}

interface NavigationItemsProps {
  menus: MenuItem[];
  collapsed?: boolean;
}

interface NavigationItemProps {
  menu: MenuItem;
  className?: string;
  collapsed?: boolean;
}

const NavigationItems = ({ menus, collapsed = false }: NavigationItemsProps) => {
  return (
    <ul role="list" className={cn(
      "flex flex-1 flex-col gap-0.5",
      collapsed ? "items-center" : ""
    )}>
      {menus.map((menu) => (
        <li key={menu.name} className={collapsed ? "w-full flex justify-center items-center" : ""}>
          <NavigationItem menu={menu} collapsed={collapsed} />
          {!collapsed && menu.items && (
            <ul className="flex flex-col gap-0.5 mt-0.5">
              {menu.items.map((subitem) => (
                <li key={subitem.name}>
                  <NavigationItem menu={subitem} className="pl-9" collapsed={collapsed} />
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
};

const NavigationItem = ({ menu, className, collapsed = false }: NavigationItemProps) => {
  const [helpOpen, setHelpOpen] = useState(false);

  const handleItemClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (menu.name === 'Help') {
      e.preventDefault();
      setHelpOpen(true);
      return;
    }

    menu.onClick?.(e);
  };

  const NavigationContent = () => (
    <div className={cn(
      "relative group/navitem",
      collapsed ? "w-full flex items-center justify-center" : ""
    )}>
      {menu.disabled ? (
        <div
          className={cn(
            "flex items-center rounded-lg text-[13px] text-zinc-500 cursor-not-allowed",
            collapsed ? "justify-center p-2 mx-auto w-10 h-10" : "px-2.5 py-[7px] gap-2.5",
            className
          )}
        >
          {menu.icon && (
            <menu.icon
              className="h-[18px] w-[18px] shrink-0 text-zinc-600"
              strokeWidth={1.5}
              aria-hidden="true"
            />
          )}
          {!collapsed && <span>{menu.name}</span>}
        </div>
      ) : (
        <Link
          to={menu.href}
          onClick={handleItemClick}
          className={cn(
            "flex items-center rounded-lg text-[13px] transition-all duration-200",
            collapsed
              ? "justify-center p-2 mx-auto w-10 h-10"
              : "px-2.5 py-[7px] gap-2.5",
            menu.active
              ? 'bg-white/15 text-white font-medium shadow-[0_1px_3px_rgba(0,0,0,0.2)]'
              : 'text-zinc-100 hover:text-white hover:bg-white/10',
            className
          )}
        >
          {menu.icon && (
            <menu.icon
              className={cn(
                'h-[18px] w-[18px] shrink-0 transition-colors duration-200 group-hover/navitem:[stroke-width:2]',
                menu.active
                  ? 'text-crimson-300 drop-shadow-[0_0_6px_rgba(236,50,87,0.65)]'
                  : 'text-zinc-200 group-hover/navitem:text-crimson-300'
              )}
              strokeWidth={menu.active ? 2 : 1.5}
              aria-hidden="true"
            />
          )}
          {!collapsed && (
            <div className="flex min-w-0 flex-1 items-center justify-between gap-1.5">
              <span className="truncate">{menu.name}</span>
              {menu.label && (
                <span className="shrink-0 rounded-sm bg-white/10 px-1 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-zinc-100">
                  {menu.label}
                </span>
              )}
            </div>
          )}
        </Link>
      )}
    </div>
  );

  // When collapsed, wrap with tooltip
  if (collapsed) {
    return (
      <>
        {menu.name === 'Help' ? (
          <HomeHelp 
            trigger={
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div 
                      role="button"
                      tabIndex={0}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-block"
                    >
                      <NavigationContent />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="text-xs font-medium bg-zinc-900 text-white border-zinc-800 shadow-lg">
                    <p>{menu.name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            } 
            isOpen={helpOpen} 
            onOpenChange={setHelpOpen} 
          />
        ) : (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div 
                  role="button"
                  tabIndex={0}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-block"
                >
                  <NavigationContent />
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs font-medium bg-zinc-900 text-white border-zinc-800 shadow-lg">
                <p>{menu.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </>
    );
  }

  // Regular rendering when not collapsed
  return (
    <>
      {menu.name === 'Help' ? (
        <HomeHelp trigger={<NavigationContent />} isOpen={helpOpen} onOpenChange={setHelpOpen} />
      ) : (
        <NavigationContent />
      )}
    </>
  );
};

export default NavigationItems;
